import React, { useContext, useEffect, useRef, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import NS00041 from "assets/img/NS-00041.jpg";
import ProfileImage from "assets/img/profile.png";
import defaultImage from "assets/img/default-img.jpg";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CPDStatusTimelineItem from "./CPDStatusTimelineItem";
import { getIcon } from "utils/iconsConfig";
import CommentSection from "components/ui/commentSection/comentSection";
import CommentComp from "components/ui/commentInput/CommentComp";
import SelectDesignModal from "components/ui/ServiceRequestFlowModals/SelectDesignModal/SelectDesignModal";
import { SettingContext } from "context/SettingContext";
import EditSelectModal from "components/ui/ServiceRequestFlowModals/EditModal/EditSelectModal";
import { Box, Grow, IconButton, Modal } from "@mui/material";
import TableContainerComp from "components/ui/UploadImageModal/TableContainerComp";
import { useGlobalContext } from "context/GlobalContext";
import { updateServiceRequest, uploadImageInServiceRequest, useServiceRequestDetails, useUploadServiceRequestImages } from "./API/getAllServiceRequest";
import { useParams } from "react-router-dom";
import { formatDate, formatDateWithSuffix } from "utils/formatDate";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "lib/api-client";
import { toast } from "react-toastify";
import AudioPlayer from "components/ui/commentSection/audioPlayer/AudioPlayer";
import ImageCardSkeleton from "components/ui/ServiceRequestFlowModals/Skeleton/ImageSkeletonModal";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";
import Webcam from "react-webcam";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { ReactComponent as UploadIcon } from "assets/icons/UploadArrowIcon.svg";

/* ---------------- DEMO DATA ---------------- */

const status_icon = (variant) => ({
  review: getIcon("ReviewIcon"),
  accepted: variant === "success" ? getIcon("AcceptedIcon", "green") : getIcon("AcceptedIcon", "grey"),
  received: variant === "success" ? getIcon("ReceivedNewIcon", "green") : getIcon("ReceivedNewIcon", "grey"),
  production: variant === "success" ? getIcon("ProductionIcon", "green") : getIcon("ProductionIcon", "grey"),
  needApproval: variant === "success" ? getIcon("NeedYourApprovalIcon", "green") : getIcon("NeedYourApprovalIcon", "grey"),
  readyDispatch: variant === "success" ? getIcon("ReadyForDispatchIcon", "green") : getIcon("ReadyForDispatchIcon", "grey"),
  dispatched: variant === "success" ? getIcon("DispatchedIcon", "green") : getIcon("DispatchedIcon", "grey"),
});

const Detail = ({ icon, label, rightIcon, onRightIconClick }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    {icon}
    <span style={{ fontFamily: "Karla", fontWeight: 400, fontSize: "16px", lineHeight: "22px", letterSpacing: "-0.41px" }}>{label}</span>

    {rightIcon && (
      <span
        onClick={onRightIconClick}
        style={{
          marginLeft: "4px",
          cursor: "pointer",
          display: "flex",
        }}
      >
        {rightIcon}
      </span>
    )}
  </div>
);

const getTimeDifference = (isoDate) => {
  const start = new Date(isoDate);
  const end = new Date();

  let diffMs = end - start;
  if (diffMs < 0) return "0M";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  let result = "";
  if (days) result += `${days}D `;
  if (hours) result += `${hours}H `;
  if (minutes) result += `${minutes}M`;

  return result.trim() || "0M";
};

/* ---------------- COMPONENT ---------------- */

const ServiceRequestDetails = () => {
  const [index, setIndex] = useState(0);
  const [binnysIndex, setBinnysIndex] = useState(0);
  const [hover, setHover] = useState(false);

  const [isDetail, setIsDetail] = useState(false);
  const [uspDetailData, setUspDetailData] = useState(null);
  const { addModalOpen, setAddModalOpen } = useContext(SettingContext);

  const [editModalType, setEditModalType] = useState(null);
  const [activeField, setActiveField] = useState(null);

  const [openUploadImage, setOpenUploadImage] = useState(false);
  const fileInputRef = useRef(null);
  const commentSectionRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const webcamRef = useRef(null);
  const [tempCapturedImage, setTempCapturedImage] = useState(null);
  const [isHoveringCaptured, setIsHoveringCaptured] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const singlefileInputRef = useRef(null);
  const [recordedData, setRecordedData] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const { serviceRequestId } = useParams();
  const permission = usePermissions();
  const hasAccessManagementAction = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Service Requests"), "management");
  const { setCurrentStatus, setApprovalTime, setShowProductionImages, scrollToBottomTrigger,setScrollToBottomTrigger, setCanReOpen ,setServiceRequestDetail } = useGlobalContext();

  const queryClient = useQueryClient();

  const { mutate: updateRequest, isLoading: isUpdating } = useMutation({
    mutationFn: updateServiceRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(["service-request-details"]);
      setEditModalType(null);
    },
  });

  const { data, isLoading, isError, error, refetch } = useServiceRequestDetails(serviceRequestId);

  const commentUser = [{ _id: data?.clientId, fullName: "Client" }];

  // const { mutate: uploadImages, isLoading: isUploadImageLoading } = useUploadServiceRequestImages();

  const { mutate: uploadImages, isPending: isUploadImageLoading } = useMutation({
    mutationFn: uploadImageInServiceRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(["service-request-details"]);
    },
  });

  useEffect(() => {
    if (!data?.statusTimeline?.length) {
      setApprovalTime(null);
      return;
    }

    const approvalStatus = [...data.statusTimeline].reverse().find((item) => item.status === "Need your Approval");

    if (approvalStatus) {
      setApprovalTime(getTimeDifference(approvalStatus.date));
    } else {
      setApprovalTime(null);
    }
  }, [data?.statusTimeline]);

  // Image Data
  const images = data?.imageUrls?.length > 0 ? data.imageUrls : [defaultImage];
  const binnysImages = data?.binnysImageUrls?.length > 0 ? data.binnysImageUrls : [defaultImage];

  // Details Data
  const { categoryName, designNo, serviceType: serviceNewType, createdAt, clientNumber } = data || {};

  useEffect(() => {
    if (data?.currentStatus) {
      setServiceRequestDetail(data);
      setCurrentStatus(data?.currentStatus);
      setCanReOpen(data?.canReOpen);
      setShowProductionImages((data?.binnysImageUrls?.length ?? 0) > 0);
    }
    return () => {
      setCurrentStatus("");
    };
  }, [data, setCurrentStatus]);

  // useEffect(() => {
  //   if (scrollToBottomTrigger && commentSectionRef.current) {
  //     commentSectionRef.current.scrollTo({
  //       top: commentSectionRef.current.scrollHeight,
  //       behavior: "smooth"
  //     });
  //   }
  // }, [scrollToBottomTrigger]);

  const status_data = [
    {
      status: "Review",
      date: "",
      icon: "review",
    },
    {
      status: "Accepted",
      date: "",
      icon: "accepted",
      // description: "(Pickup - BVC Logistics)",
    },
    {
      status: "Received",
      date: "",
      icon: "received",
    },
    {
      status: "Production",
      date: "",
      icon: "production",
    },
    {
      status: "Need your Approval",
      date: "",
      icon: "needApproval",
    },
    {
      status: "Ready for Dispatch",
      date: "",
      icon: "readyDispatch",
    },
    {
      status: "Dispatched",
      date: "",
      icon: "dispatched",
      // description: "(Delivery - BBQ Logistics)",
    },
  ];

  // console.log("status_data=====>>>>>",status_data)

  const permissions=usePermissions();

  const getLatestReviewIndex = (timeline) => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].status === "Review") {
        return i;
      }
    }
    return 0;
  };

  const IGNORED_STATUSES = ["Rejected", "Cancelled"];

  const finalStatusData = React.useMemo(() => {
    if (!data?.statusTimeline?.length) return status_data;

    const latestReviewIndex = getLatestReviewIndex(data.statusTimeline);

    const activeTimeline = data.statusTimeline.slice(latestReviewIndex).filter((s) => !IGNORED_STATUSES.includes(s.status));

    const latestStatusMap = {};
    activeTimeline.forEach((s) => {
      if (!latestStatusMap[s.status] || new Date(s.date) > new Date(latestStatusMap[s.status])) {
        latestStatusMap[s.status] = s.date;
      }
    });

    const byPassIndex = [...activeTimeline].map((s) => s.status).lastIndexOf("ByPass Approval");

    const hasByPassApproval = byPassIndex !== -1;

    const previousStatus = hasByPassApproval && byPassIndex > 0 ? activeTimeline[byPassIndex - 1].status : null;

    const shouldShowNeedApproval = !hasByPassApproval || previousStatus === "Need your Approval";

    const validCurrentStatus = IGNORED_STATUSES.includes(data.currentStatus) ? null : data.currentStatus;

    const currentStatusIndex = validCurrentStatus ? status_data.findIndex((s) => s.status === validCurrentStatus) : -1;

    return status_data.map((item, index) => {
      if (currentStatusIndex !== -1 && index > currentStatusIndex) {
        return { ...item, date: "" };
      }

      if (item.status === "Need your Approval" && !shouldShowNeedApproval) {
        return { ...item, date: "" };
      }

      let date = latestStatusMap[item.status] || "";

      if (item.status === "Need your Approval" && hasByPassApproval && shouldShowNeedApproval) {
        date = latestStatusMap["ByPass Approval"];
      }

      return {
        ...item,
        date: date ? formatDateWithSuffix(date) : "",
      };
    });
  }, [data]);

  // console.log("finalStatusData=====>>>",finalStatusData)

  const handleUpload = () => {
    if (selectedFiles.length > 5) {
      alert("Max allowed 5 Images");
      return;
    }
    uploadImages(
      {
        serviceRequestId: serviceRequestId || "",
        images: selectedFiles, // File[]
      },
      {
        onSuccess: (data) => {
          toast.success("File uploaded successfully");
          console.log("Upload success", data);
          setSelectedFiles([]);
          setOpenUploadImage(false);
        },
        onError: (error) => {
          console.error("Upload failed", error);
          toast.error("File upload failed");
        },
      }
    );
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setSelectedFiles((prev) => {
      const existing = prev.map((f) => f.name + f.size);
      const filtered = files.filter((f) => !existing.includes(f.name + f.size));
      return [...prev, ...filtered];
    });

    setOpenUploadImage(true);
    e.target.value = "";
  };

  const handleDeleteImg = (index) => {
    if (selectedFiles.length === 1) {
      setSelectedFiles([]);
      setOpenUploadImage(false); // 🔥 MODAL CLOSE
      return;
    }

    // NORMAL DELETE
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const prevImage = (type) => {
    if (type === "binnysImage") {
      setBinnysIndex((prev) => (prev === 0 ? binnysImages.length - 1 : prev - 1));
    } else {
      setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  };

  const nextImage = (type) => {
    if (type === "binnysImage") {
      setBinnysIndex((prev) => (prev === binnysImages.length - 1 ? 0 : prev + 1));
    } else {
      setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  };

  const handleSaveParent = async (styleNumber) => {
    let paylaod = {
      designNo: styleNumber,
      serviceRequestId: serviceRequestId,
    };
    let data = await apiClient.post("/api/v9/serviceRequest/updateServiceRequestVision", paylaod);

    if (data?.data?.success) {
      // toast.success(data?.data?.message, 300);
      refetch();
    }
  };

  // Webcam functions
  const handleCapture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;
    setTempCapturedImage(imageSrc);
  };

  const handleAcceptImage = async () => {
    if (!tempCapturedImage) return;

    const res = await fetch(tempCapturedImage);
    const blob = await res.blob();

    const file = new File([blob], `camera-${Date.now()}.png`, {
      type: "image/png",
    });

    const newFiles = [...selectedFiles, file];

    // if (newFiles.length > 5) {
    //   alert("Max allowed 5 Images");
    //   return;
    // }

    setTempCapturedImage(null);
    setShowCamera(false);
    setSelectedFiles(newFiles);
    setOpenUploadImage(true);
  };

  const handleRejectImage = () => {
    setTempCapturedImage(null);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleSingleFileClick = () => {
    singlefileInputRef.current?.click();
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  useEffect(() => {
    return () => {
      setScrollToBottomTrigger(false)
    }
  }, [])

  if (isLoading)
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        {" "}
        <ImageCardSkeleton />{" "}
      </div>
    );
  if (isError) return <p>Error loading details</p>;
  return (
    <div style={{ background: "#000", color: "#fff", padding: "16px" }}>
      {/* ================== IMAGE SECTION ================== */}
      <div
        style={{
          background: "#000",
          padding: "12px",
          borderRadius: "8px",
          marginBottom: "16px",
          display: "flex",
          justifyContent: "center",
          // border: "1px solid red",
          position: "relative",
          ...(data?.currentStatus === "Production" || data?.currentStatus === "Need your Approval" || data?.currentStatus === "Ready for Dispatch" || data?.currentStatus === "Dispatched"
            ? {
              width: "100%",
              gap: "51px",
            }
            : {
              width: "50%",
              flexDirection: "column",
              alignItems: "center",
              margin: "0px auto",
            }),
        }}
      >
        <div style={{ position: "relative", paddingBottom: "8px" }}>
          <div style={{ width: "670px", display: "flex" }}>
            <p style={{ fontSize: "22px", marginTop: 0, marginBottom: "8px", color: "#fff", fontFamily: "Poppins", fontWeight: 500 }}>Uploaded by Client</p>
          </div>

          <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              position: "relative",
              height: "420px",
              background: "#111",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "670px",
              height: "670px",
              // border: "1px solid red",
            }}
          >
            {/* IMAGE */}
            <img
              src={images[index]}
              alt="design"
              style={{
                height: "100%",
                width: "100%",
                borderRadius: "6px",
                objectFit: "contain",
                display: "block",
              }}
            />

            {/* LEFT ARROW */}
            {hover && images.length > 1 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: "absolute",
                  left: "12px",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: index === 0 ? "default" : "pointer",
                  opacity: index === 0 ? 0.3 : 1,
                  pointerEvents: index === 0 ? "none" : "auto",
                }}
              >
                <ArrowBackIosNewIcon sx={{ color: "#fff", fontSize: 18 }} />
              </div>
            )}

            {/* RIGHT ARROW */}
            {hover && images.length > 1 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: "absolute",
                  right: "12px",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: index === images.length - 1 ? "default" : "pointer",
                  opacity: index === images.length - 1 ? 0.3 : 1,
                  pointerEvents: index === images.length - 1 ? "none" : "auto",
                }}
              >
                <ArrowForwardIosIcon sx={{ color: "#fff", fontSize: 18 }} />
              </div>
            )}
          </div>
          {/* DOT INDICATORS */}
          {images?.length > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "0px",
                display: "flex",
                gap: "6px",
                margin: "0px auto",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {images.map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: index === i ? "#fff" : "#555",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {(data?.currentStatus === "Production" || data?.currentStatus === "Need your Approval" || data?.currentStatus === "Ready for Dispatch" || data?.currentStatus === "Dispatched") && (
          <div style={{ width: "fit-content", display: "flex", flexDirection: "column" }}>
            {data?.binnysImageUrls?.length > 0 ? (
              <>
                <div style={{ position: "relative", paddingBottom: "8px" }}>
                  <div style={{ width: "670px", display: "flex" }}>
                    <p style={{ fontSize: "22px", marginTop: 0, marginBottom: "8px", color: "#fff", fontFamily: "Poppins", fontWeight: 500 }}>Uploaded by Binny’s</p>
                  </div>

                  <div
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    style={{
                      position: "relative",
                      height: "420px",
                      background: "#111",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "670px",
                      height: "670px",
                      // border: "1px solid red",
                    }}
                  >
                    {/* IMAGE */}
                    <img
                      src={binnysImages[binnysIndex]}
                      alt="design"
                      style={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "6px",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />

                    {/* LEFT ARROW */}
                    {hover && binnysImages.length > 1 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          prevImage("binnysImage");
                        }}
                        style={{
                          position: "absolute",
                          left: "12px",
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: binnysIndex === 0 ? "default" : "pointer",
                          opacity: binnysIndex === 0 ? 0.3 : 1,
                          pointerEvents: binnysIndex === 0 ? "none" : "auto",
                        }}
                      >
                        <ArrowBackIosNewIcon sx={{ color: "#fff", fontSize: 18 }} />
                      </div>
                    )}

                    {/* RIGHT ARROW */}
                    {hover && binnysImages.length > 1 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          nextImage("binnysImage");
                        }}
                        style={{
                          position: "absolute",
                          right: "12px",
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: binnysIndex === binnysImages.length - 1 ? "default" : "pointer",
                          opacity: binnysIndex === binnysImages.length - 1 ? 0.3 : 1,
                          pointerEvents: binnysIndex === binnysImages.length - 1 ? "none" : "auto",
                        }}
                      >
                        <ArrowForwardIosIcon sx={{ color: "#fff", fontSize: 18 }} />
                      </div>
                    )}
                  </div>
                  {/* DOT INDICATORS */}
                  {binnysImages?.length > 1 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "0px",
                        display: "flex",
                        gap: "6px",
                        margin: "0px auto",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {binnysImages.map((_, i) => (
                        <span
                          key={i}
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: binnysIndex === i ? "#fff" : "#555",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={{ width: "670px", display: "flex" }}>
                  <p style={{ fontSize: "22px", marginTop: 0, marginBottom: "8px", color: "#fff", fontFamily: "Poppins", fontWeight: 500 }}>Uploaded by Binny’s</p>
                </div>
                  {/* Upload Box */}
                  <div style={{ width: "670px", display: "flex", justifyContent: "center" }}>
                    {(showCamera || selectedFiles.length === 0) && !tempCapturedImage ? (
                      <div
                        style={{
                          width: "670px",
                          height: "670px",
                          border: "1px solid #777",
                          borderRadius: 10,
                          position: "relative",
                          overflow: "hidden",
                          background: "#000",
                        }}
                      >
                        <Webcam
                          ref={webcamRef}
                          audio={false}
                          screenshotFormat="image/png"
                          videoConstraints={{ width: 1280, height: 720 }}
                          onUserMediaError={(err) => {
                            if (err.name === "NotAllowedError") {
                              alert("Permission denied");
                            } else if (err.name === "NotFoundError") {
                              alert("No camera device")
                            }
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            background: "#000",
                          }}
                        />

                        {/* Capture Button */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 20,
                            left: "50%",
                            transform: "translateX(-50%)",
                          }}
                        >
                          <button
                            onClick={handleCapture}
                            style={{
                              width: 70,
                              height: 70,
                              borderRadius: "50%",
                              border: "4px solid white",
                              background: "transparent",
                              cursor: "pointer",
                            }}
                          />
                        </div>

                        {/* Upload Icon Top Right */}
                        <IconButton
                          onClick={handleFileClick}
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            color: "#fff",
                            zIndex: 10,
                          }}
                        >
                          <UploadIcon />
                        </IconButton>

                        <input type="file" ref={fileInputRef} multiple accept=".jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleFileChange} />
                      </div>
                    ) : tempCapturedImage ? (
                      <div
                        style={{
                          width: "670px",
                          height: "670px",
                          border: "1px solid #777",
                          borderRadius: 10,
                          position: "relative",
                          overflow: "hidden",
                          background: "#000",
                        }}
                        onMouseEnter={() => setIsHoveringCaptured(true)}
                        onMouseLeave={() => setIsHoveringCaptured(false)}
                      >
                        <img
                          src={tempCapturedImage}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />


                        <div
                          sx={{
                            position: "relative",
                            width: 350,
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              width: "100%",
                              height: 70,
                              background: "#191818BF", // 👈 GREY like your image
                              backdropFilter: "blur(6px)", // 👈 smooth glass effect
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-around",
                            }}
                          >
                            {/* ❌ Reject */}
                            <IconButton
                              onClick={handleRejectImage}
                              sx={{
                                color: "white",
                                "&:hover": {
                                  background: "#191818BF",
                                },
                              }}
                            >
                              <CloseIcon fontSize="large" />
                            </IconButton>

                            {/* ✔ Accept */}
                            <IconButton
                              onClick={handleAcceptImage}
                              sx={{
                                color: "white",
                                "&:hover": {
                                  background: "rgba(255,255,255,0.1)",
                                },
                              }}
                            >
                              <CheckIcon fontSize="large" />
                            </IconButton>
                          </Box>
                        </div>

                        <input type="file" ref={singlefileInputRef} multiple accept=".jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleFileChange} />
                      </div>
                    ) : (
                      <div
                        style={{
                          height: "670px",
                          width: "670px",
                          border: "1px solid #777676ff",
                          borderRadius: 10,
                          display: "flex",
                          flexDirection: "column",
                          overflowY: "auto",
                          cursor: "pointer",
                        }}
                      >
                        {selectedFiles.map((item, index) => {
                          const previewSrc = URL.createObjectURL(item);

                          return (
                            <div
                              key={index}
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "1px solid #444",
                                borderRadius: 8,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                flexShrink: 0,
                                position: "relative",
                              }}
                              onMouseEnter={() => setHoveredIndex(index)}
                              onMouseLeave={() => setHoveredIndex(null)}
                              onClick={handleFileClick}
                            >
                              {/* Remove Button */}
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFile(index);
                                }}
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  background: "rgba(0, 0, 0, 0.7)",
                                  color: "#fff",
                                  zIndex: 999,
                                  padding: "4px",
                                }}
                                size="small"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>

                              {hoveredIndex === index && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    background: "rgba(0, 0, 0, 0.7)",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    zIndex: 15,
                                    transition: "all 0.3s ease",
                                  }}
                                  onClick={handleSingleFileClick}
                                >
                                  <p
                                    style={{
                                      color: "#fff",
                                      fontSize: 14,
                                      fontWeight: 500,
                                      fontFamily: "Poppins",
                                    }}
                                  >
                                    {getIcon("UploadIcon", "white")}
                                  </p>
                                  <p
                                    style={{
                                      color: "#FFFFFF",
                                      fontSize: 24,
                                      marginTop: 0,
                                      fontFamily: "Karla",
                                    }}
                                  >
                                    Upload
                                  </p>
                                </div>
                              )}

                              {/* Image Preview - Full Box */}
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  background: "#000",
                                  overflow: "hidden",
                                }}
                              >
                                <img
                                  src={previewSrc}
                                  alt="preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                              <input type="file" ref={singlefileInputRef} multiple accept=".jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleFileChange} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                {/* <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    background: "#000",
                    padding: "12px",
                    borderRadius: "8px",
                    width: "670px",
                    border: "1px dashed #444",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    height: `calc(100% - 50px)`,
                  }}
                >
                  {/* Upload Area */}
                  {/* <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {getIcon("UploadIcon")}
                    <label
                      style={{
                        color: "white",
                        marginTop: "24px",
                        fontFamily: "Karla",
                        fontSize: "24px",
                        fontWeight: 500,
                      }}
                    >
                      Upload Image
                    </label>
                    <input ref={fileInputRef} type="file" id="uploadFile" multiple accept="image/png, image/jpg, image/jpeg, image/webp" style={{ display: "none" }} onChange={handleFileChange} />
                  </div> */}

                  {/* Hidden file input */}
                  {/* <input type="file" accept="image/*" style={{ display: "none" }} />
                </div> */}
              </>
            )}
          </div>
        )}
      </div>

      {/* ================== CONCERN + STATUS ================== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          background: "#000",
          padding: "16px",
          marginBottom: "16px",
          borderTop: "1px solid #444",
        }}
      >
        {/* LEFT : CONCERN / VOICE / DETAILS */}
        <div style={{ color: "#fff", display: "flex", flexDirection: "column" }}>
          {/* CONCERN */}
          <div style={{ flexGrow: 1 }}>
            <p style={{ fontSize: "20px", fontFamily: "Poppins", lineHeight: "15px", letterSpacing: "-0.41px" }}>Concern</p>
            <div
              style={{
                background: "#1a1a1a",
                padding: "14px",
                borderRadius: "8px",
                fontSize: "16px",
                color: "#fff",
                marginBottom: "18px",
                minHeight: "150px",
                maxHeight: "150px",
                height: "150px",
                fontFamily: "Karla",
                fontWeight: 500,
                overflow: "auto",
              }}
            >
              {data?.concern || "No concern provided"}
            </div>
          </div>

          {/* VOICE NOTE */}
          {data?.voiceNote && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p style={{ fontSize: "20px", fontFamily: "Poppins", lineHeight: "15px", letterSpacing: "-0.41px" }}>Concern Voicenote</p>
              <AudioPlayer src={data.voiceNote} />
            </div>
          )}

          {/* DIVIDER */}
          <div
            style={{
              height: "1px",
              background: "#444444",
              margin: "18px 0",
            }}
          />

          {/* DETAILS GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              rowGap: "14px",
              fontSize: "16px",
              //   border: "1px solid blue"
            }}
          >
            {/* <Detail icon={getIcon("BlueRingIcon")} label="GB-00032" rightIcon={getIcon("EditIcon")} onRightIconClick={() => {  setEditType("DESIGN");  setEditModalOpen(true); }}/> */}

            <Detail
              icon={getIcon("BlueRingIcon")}
              label={designNo || "N/A"}
              rightIcon={hasAccessManagementAction ? getIcon("EditIcon"): null}
              onRightIconClick={() => {
                setIsDetail(true);
                setUspDetailData({ styleNumber: "GB-00032" });
                setAddModalOpen(true);
              }}
            />
            <Detail
              icon={getIcon("GreenCategoryIcon")}
              label={categoryName || "-"}
              rightIcon={hasAccessManagementAction ? getIcon("EditIcon"): null}
              onRightIconClick={() => {
                setActiveField("CATEGORY");
                setEditModalType("CATEGORY");
              }}
            />
            <Detail icon={getIcon("BluePhoneIcon")} label={clientNumber || "-"} />

            <Detail icon={getIcon("YellowPinCodeIcon")} label={`#${serviceRequestId || "-"}`} />
            <Detail
              icon={getIcon("PinkRepairIcon")}
              label={serviceNewType || "-"}
              rightIcon={hasAccessManagementAction ? getIcon("EditIcon"): null}
              onRightIconClick={() => {
                setActiveField("SERVICE");
                setEditModalType("SERVICE");
              }}
            />
            <Detail icon={getIcon("YellowDateIcon")} label={createdAt ? formatDate(createdAt) : "-"} />
          </div>
        </div>

        {/* RIGHT : STATUS TIMELINE */}
        <div
          style={{
            width: "100%",
            backgroundColor: "#191818",
            borderRadius: "5px",
            padding: "6px 16px",
          }}
        >
          <div
            className="cpd-review-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "5px" }}>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  fontFamily: "Poppins",
                }}
              >
                Service Request Status
              </p>
              <p style={{ fontSize: "18px", color: "#8B8B8B" }}>{`(#${serviceRequestId})`}</p>
            </div>
            <span
              style={{
                fontSize: "13px",
                color: data?.currentStatus === "Cancelled" ? "#E77B7B" : "#50C878",
                fontFamily: "Poppins",
                fontWeight: 500,
                backgroundColor: data?.currentStatus === "Cancelled" ? "#E77B7B26" : "#50C8781A",
                borderRadius: "4px",
                // width: "66px",
                height: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "0px 10px",
              }}
            >
              {data?.currentStatus}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              marginTop: "16px",
            }}
          >
            {finalStatusData?.map((cpd, index) => (
              <CPDStatusTimelineItem
                item={cpd}
                index={index}
                totalItems={finalStatusData.length}
                icons={status_icon(cpd.date !== "" ? "success" : "default")[cpd.icon]}
                isCompleted={cpd.date !== ""}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ================== ③ CHAT SECTION ================== */}
      <div
        style={{
          // background: "#111",
          borderTop: "1px solid #444",
          padding: "16px",
          // borderRadius: "8px",
        }}
      >
        <div style={{ maxHeight: "600px", overflowY: "scroll", scrollbarWidth: "none" }}>
          <CommentSection commentUser={commentUser} />
        </div>
        <div>
          <CommentComp openUploadImage={openUploadImage} setOpenUploadImage={setOpenUploadImage} commentUser={commentUser} />
        </div>
      </div>
      <SelectDesignModal open={addModalOpen} onClose={() => setAddModalOpen(false)} isDetail={isDetail} uspDetailData={uspDetailData} setIsDetail={setIsDetail} handleSaveParent={handleSaveParent} />

      <EditSelectModal
        open={Boolean(editModalType)}
        onClose={() => setEditModalType(null)}
        title={activeField === "CATEGORY" ? "Edit Category" : "Modify Service Request"}
        value={activeField === "CATEGORY" ? categoryName : serviceNewType}
        name={activeField === "CATEGORY" ? "category" : "serviceType"}
        options={
          activeField === "CATEGORY"
            ? [
              "Bali",
              "Bands",
              "Bangles",
              "Brooches",
              "Buttons",
              "Charms",
              "Cuff Links",
              "Earrings",
              "Gents Bracelets",
              "Gents Chains",
              "Gents Pendants",
              "Gents Rings",
              "Gold Belts",
              "Gold Gents Bracelet",
              "Gold Gents Watches",
              "Gold Ladies Bracelets",
              "Gold Ladies Watches",
              "Ladies Bracelets",
              "Ladies Chains",
              "Ladies Pendants",
              "Ladies Rings",
              "Mangalsutra",
              "Necklace",
              "Nose Pin",
              "Other",
              "Pens",
              "Steel Belts",
              "Steel Frame",
              "Steel Mobile",
              "Steel Watches",
            ]
            : ["Manufacturing Defects", "QC Failed", "Repairing Request"]
        }
        onSave={(val) => {
          if (!serviceRequestId) return;

          const payload = {
            serviceRequestId,
          };

          if (activeField === "CATEGORY") {
            payload.categoryName = val;
          }

          if (activeField === "SERVICE") {
            payload.serviceType = val;
          }

          updateRequest(payload);
        }}
      />

      {/* Upload Modal */}
      <Modal
        open={openUploadImage}
        onClose={() => {
          setOpenUploadImage(false);
        }}
      >
        
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
              background: "#101010",
            }}
          >
            <TableContainerComp
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              handleFileChange={handleFileChange}
              onClose={() => setOpenUploadImage(false)}
              handleDeleteImg={handleDeleteImg}
              AddImagesBtn={true}
              submitBtnName={"Upload"}
              handleSubmitBtn={handleUpload}
              isLoading={isUploadImageLoading}
              isEditIcon={false}
              onOpenCamera={() => {
                setOpenUploadImage(false);
                setShowCamera(true);
              }}
            />
          </div>
        
      </Modal>
    </div>
  );
};

export default ServiceRequestDetails;
